import { buildBaseUrl } from "../config/config";
import { SIGNUP_STEPS, SignupStep, trackSignupStep } from "../api/auth/signup";

/**
 * Identifiant de tunnel.
 *
 * Il est tiré au premier clic sur un CTA de la landing (paramètre `fid` de
 * l'URL) et conservé pour toute la session : c'est lui qui relie le clic, les
 * étapes du formulaire, la création des comptes puis l'activation — y compris
 * pour les visiteurs qui abandonnent avant d'avoir un compte.
 */
const FUNNEL_STORAGE_KEY = "tc_signup_funnel_id";

const randomFunnelId = (): string => {
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }
    } catch {
        /* environnements sans crypto.randomUUID */
    }
    return `f-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/** Récupère l'identifiant de tunnel courant, en le créant si besoin. */
export const resolveFunnelId = (fromUrl?: string | null): string => {
    const candidate = (fromUrl || "").trim();
    try {
        if (candidate) {
            window.sessionStorage?.setItem(FUNNEL_STORAGE_KEY, candidate);
            return candidate;
        }
        const stored = window.sessionStorage?.getItem(FUNNEL_STORAGE_KEY);
        if (stored) return stored;
        const generated = randomFunnelId();
        window.sessionStorage?.setItem(FUNNEL_STORAGE_KEY, generated);
        return generated;
    } catch {
        // Navigation privée ou stockage bloqué : un identifiant volatil suffit,
        // le tunnel restera cohérent le temps du montage de la page.
        return candidate || randomFunnelId();
    }
};

/** Oublie le tunnel courant (après une inscription menée à son terme). */
export const clearFunnelId = (): void => {
    try {
        window.sessionStorage?.removeItem(FUNNEL_STORAGE_KEY);
    } catch {
        /* sans effet si le stockage est indisponible */
    }
};

export type SignupTracker = (
    step: SignupStep,
    extra?: {
        method?: string;
        email?: string;
        country?: string;
        metadata?: Record<string, unknown>;
    }
) => void;

/**
 * Fabrique un traceur d'étapes lié à un plan et un identifiant de tunnel.
 * Les envois sont silencieux : le monitoring ne doit jamais bloquer l'inscription.
 */
export const createSignupTracker = (plan: string, funnelId: string): SignupTracker => {
    const baseUrl = buildBaseUrl("identity");
    return (step, extra) =>
        trackSignupStep(baseUrl, {
            funnel_id: funnelId,
            plan,
            step,
            method: extra?.method,
            email: extra?.email,
            country: extra?.country,
            metadata: extra?.metadata,
        });
};

export { SIGNUP_STEPS };
export type { SignupStep };
